import React from 'react';
import { LinkText, Table } from '@adc/parallax-component-library';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import { Device } from '../../models';
import i18n from '../../utilities/i18n';
import { UploadHistoryModal } from '../modal/UploadHistoryModal';
import DateCellRenderer from '../table/DateCellRenderer';

type DevicesTableProps = {
  title: string;
  description: string;
  isLoading: boolean;
  hasLoadingError: boolean;
  devices: Device[];
  navigateToDevice: (deviceKey: string, deviceLabel: string) => void;
  showUploadHistory?: boolean;
  toolbarRightContent?: React.ReactNode;
};

/**
 * DevicesTable component displays a table of devices associated with a specific subject.
 * @param {string} title - The title of the table.
 * @param {string} description - The description of the table.
 * @param {boolean} isLoading - Indicates if the devices data is currently being loaded.
 * @param {boolean} hasLoadingError - Indicates if there was an error loading the devices data.
 * @param {Device[]} devices - An array of Device objects to display in the table.
 * @param {(deviceKey: string, deviceLabel: string) => void} navigateToDevice - A callback function to navigate to a specific device's details page.
 * @param {boolean} showUploadHistory - Determines if table allows viewing upload history.
 * @param {React.ReactNode} toolbarRightContent - Content to display on the right side of the table toolbar.
 * @returns {React.JSX.Element}
 */
export const DevicesTable = ({
  title,
  description,
  isLoading,
  hasLoadingError,
  devices,
  navigateToDevice,
  showUploadHistory = false,
  toolbarRightContent,
}: DevicesTableProps) => {
  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Device.appName'), dataKey: 'appName' },
    { label: i18n.t('Device.id'), dataKey: 'deviceId' },
    { label: i18n.t('Device.os'), dataKey: 'os' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
    {
      label: i18n.t('Device.appVersion'),
      dataKey: 'bundleVersion',
    },
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
  ];

  if (showUploadHistory) {
    devicesTableColumnData.push({
      label: '',
      dataKey: 'uploadHistory',
      isSortable: false,
    });
  }

  const devicesTableCellRenderer = (dataKey: string, device: Device) => {
    if (dataKey === 'createdOn') {
      return <DateCellRenderer value={device.createdOn} key={dataKey} />;
    } else if (dataKey === 'uploadHistory') {
      return (
        <Table.TextCell key={dataKey}>
          <UploadHistoryModal
            deviceId={device.key || ''}
            deviceName={device.appName || ''}
            trigger={
              <LinkText
                onPress={() => {}}
                type="$bodySmall.default"
                testID={`upload-history-button-${device.key}`}
              >
                {i18n.t('UploadHistory.actionLabel')}
              </LinkText>
            }
          />
        </Table.TextCell>
      );
    } else if (dataKey === 'appName') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToDevice(device.key, device.appName)}
            type="$bodySmall.default"
          >
            {device[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{device[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <DynamicTable
      testID="devices-table"
      isLoading={isLoading}
      hasLoadingError={hasLoadingError}
      title={title}
      description={description}
      columnData={devicesTableColumnData}
      rowData={devices}
      cellRenderer={devicesTableCellRenderer}
      toolbarRightContent={toolbarRightContent}
    />
  );
};
